import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import { HouseConfigSchema, type HouseConfig } from './schema';
import { upgradeToLatest } from './upgrades';

/**
 * URL-hash codec for HouseConfig.
 *
 * Why URL hash (not query)
 * ------------------------
 * - Static hosting (nginx) doesn't route on hash, so deep links are cheap.
 * - Changing the hash never reloads the page, so copy-to-share never
 *   disrupts the live 3D scene.
 *
 * Payload format: `v1.<lz-string compressed JSON>`. The leading `v1.` is a
 * codec tag, NOT the schema version — schemaVersion lives inside the JSON.
 * Bumping the codec tag lets us change compression strategy later without
 * breaking old links.
 */

const CODEC_TAG = 'v1';

export function encodeToHash(config: HouseConfig): string {
  const json = JSON.stringify(config);
  const compressed = compressToEncodedURIComponent(json);
  return `${CODEC_TAG}.${compressed}`;
}

export function decodeFromHash(hashPayload: string): HouseConfig {
  const trimmed = hashPayload.startsWith('#') ? hashPayload.slice(1) : hashPayload;
  const dot = trimmed.indexOf('.');
  if (dot < 0) {
    throw new Error('Hash payload missing codec tag.');
  }
  const tag = trimmed.slice(0, dot);
  const body = trimmed.slice(dot + 1);

  if (tag !== CODEC_TAG) {
    throw new Error(`Unsupported hash codec tag: ${tag}`);
  }

  const json = decompressFromEncodedURIComponent(body);
  if (!json) {
    throw new Error('Failed to decompress hash payload.');
  }

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch (err) {
    throw new Error(`Hash payload JSON is malformed: ${(err as Error).message}`);
  }

  const upgraded = upgradeToLatest(raw);
  const parsed = HouseConfigSchema.safeParse(upgraded.config);
  if (!parsed.success) {
    throw new Error(`Hash payload failed schema validation: ${parsed.error.message}`);
  }
  return parsed.data;
}

/** Non-throwing decode for URL-bar reads that should tolerate garbage links. */
export function tryDecodeFromHash(
  hashPayload: string,
): { ok: true; value: HouseConfig } | { ok: false; error: string } {
  try {
    return { ok: true, value: decodeFromHash(hashPayload) };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

export function buildShareUrl(config: HouseConfig, base: string = window.location.origin + window.location.pathname): string {
  return `${base}#${encodeToHash(config)}`;
}
